export const termsOfService = `# Terms of Service

**Last Updated: January 23, 2025**

## 1. Introduction

Welcome to Collabish ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our website, applications, and services (collectively, the "Service").

By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of the Terms, you may not access the Service.

## 2. Eligibility

You must be at least 13 years of age (or the minimum legal age in your jurisdiction) to use our Service. By using our Service, you represent and warrant that you meet all eligibility requirements. If you are using the Service on behalf of an entity, organization, or company, you represent and warrant that you have the authority to bind that organization to these Terms.

## 3. Account Registration

### 3.1 Account Creation
To access certain features of the Service, you may be required to register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.

### 3.2 Account Security
You are responsible for safeguarding your account credentials and for any activities or actions under your account. You agree to notify us immediately of any unauthorized use of your account. We cannot and will not be liable for any loss or damage arising from your failure to comply with this section.

### 3.3 TikTok Login
Our Service allows you to connect your account with TikTok. By connecting your account with TikTok, you agree to comply with TikTok's terms of service in addition to these Terms.

## 4. User Content

### 4.1 Content Responsibility
You are solely responsible for any content you post, upload, share, store, or otherwise provide through the Service ("User Content"). You represent and warrant that:
- You own or have the necessary rights to use and authorize us to use your User Content
- Your User Content does not violate the rights of any third party, including intellectual property rights and privacy rights
- Your User Content complies with these Terms and all applicable laws and regulations

### 4.2 Content License
By submitting User Content to the Service, you grant us a worldwide, non-exclusive, royalty-free, sublicensable, and transferable license to use, reproduce, distribute, prepare derivative works of, display, and perform the User Content in connection with the Service and our business operations.

### 4.3 Content Removal
We reserve the right, but not the obligation, to review, monitor, or remove User Content at our sole discretion for any reason or no reason, including User Content that we determine violates these Terms.

## 5. Prohibited Conduct

You agree not to engage in any of the following prohibited activities:
- Violating any applicable laws or regulations
- Infringing on the rights of others, including patent, trademark, trade secret, copyright, privacy, or other proprietary rights
- Using the Service to transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable
- Attempting to interfere with, compromise the system integrity or security, or decipher any transmissions to or from the servers running the Service
- Using any robot, spider, crawler, scraper, or other automated means to access the Service
- Bypassing measures we may use to prevent or restrict access to the Service
- Collecting or harvesting any personally identifiable information from the Service
- Impersonating another person or entity
- Engaging in any activity that could disable, overburden, damage, or impair the Service
- Using the Service for any commercial purpose, unless explicitly permitted by us
- Creating fake collaborations or fraudulent accounts
- Attempting to conduct collaborations off-platform to avoid platform fees
- Submitting misleading or false product information (for Brands)
- Failing to deliver agreed-upon content without reasonable communication (for Creators)
- Repeatedly rejecting content without legitimate reasons (for Brands)

## 6. Intellectual Property Rights

### 6.1 Our Intellectual Property
The Service and its original content (excluding User Content), features, and functionality are and will remain the exclusive property of Collabish LLC and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.

### 6.2 Feedback
If you provide feedback, ideas, or suggestions regarding our Service ("Feedback"), you grant us an unrestricted, perpetual, irrevocable, non-exclusive, fully-paid, royalty-free right to use the Feedback in any manner and for any purpose without compensation to you.

## 7. Third-Party Services and Content

### 7.1 Third-Party Services
The Service may contain links to third-party websites, applications, or services that are not owned or controlled by us. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.

### 7.2 TikTok Integration
Our Service integrates with TikTok's platform and services. Your use of such integrations is subject to TikTok's Terms of Service and other applicable policies. We are not responsible for any TikTok services, changes, or issues.

## 8. Payment Terms

### 8.1 Fees
We collect fees for transactions facilitated through our Service, including collaboration funding and payout processing.

We may also offer certain features of the Service on a subscription basis subject to fees. You agree to pay all fees specified when you select such features. Fees are non-refundable except as required by law or as otherwise specified at the time of purchase.

### 8.2 Billing
If you provide us with your credit card information, you authorize us to charge your card for all fees associated with your account. If your payment cannot be completed, we may suspend or terminate your access to paid features.

### 8.3 Platform Fees

**For Brand Users:**
When you fund a collaboration, a platform fee of 1% is added to the collaboration price at checkout. This fee is charged in addition to the collaboration price you agreed to with the Creator.

**For Creators:**
When you complete a collaboration and earnings are credited to your account, a platform fee of 1% is deducted from the collaboration price before funds are deposited to your Stripe connected account. The balance shown in your account reflects the net amount available for withdrawal after this fee has been deducted.

Platform fee percentages are subject to change with 30 days' advance notice to users. All platform fees are non-refundable except as required by law. These fees are separate from any payment processing fees charged by Stripe.

## 9. Payment Processing

### 9.1 Stripe Integration
All payments are processed through Stripe, Inc. ("Stripe"). By using our payment features, you agree to be bound by Stripe's Services Agreement (available at https://stripe.com/legal) and applicable policies. We use Stripe to process payments securely and in compliance with applicable financial regulations.

### 9.2 Creator Connected Accounts
**For Creators:** To receive payments, you must connect a Stripe account through our Service. By completing the Stripe onboarding process, you authorize us to create and manage a Stripe connected account on your behalf for the purpose of processing collaboration payments. You remain solely responsible for:
- Compliance with Stripe's terms of service and policies
- Accuracy of banking information provided to Stripe
- All tax reporting obligations related to your earnings
- Maintaining your Stripe account in good standing

### 9.3 Brand Payment Accounts
**For Brands:** You authorize us to charge your payment method on file for collaboration funding and any applicable platform fees. You are responsible for maintaining valid payment information and ensuring sufficient funds are available.

### 9.4 Payment Disputes
Payment disputes, chargebacks, and refund requests must be resolved through Stripe's dispute resolution process in accordance with Stripe's policies. We are not responsible for payment processing issues, transaction delays, chargebacks, or disputes between you and Stripe. We may provide information or assistance in resolving disputes at our discretion.

### 9.5 Payout Schedule
Creator payouts are subject to Stripe's standard payout schedules, which typically range from 2-7 business days after a payout request is initiated. Payout timing may vary based on your bank, location, and Stripe account status.

## 10. Collaboration Terms

### 10.1 Platform Role
Collabish acts as a marketplace platform connecting Brands with Creators. We are not a party to the collaboration agreements formed between Brands and Creators. We do not guarantee the quality, safety, legality, or fulfillment of any collaborations. Users enter into collaboration agreements at their own risk.

### 10.2 Brand Responsibilities
When participating in collaborations as a Brand, you agree to:
- Provide accurate and truthful product information
- Fund collaborations as agreed upon with Creators
- Review submitted content within a reasonable timeframe
- Provide products, samples, or other agreed-upon materials to Creators in a timely manner
- Reject content only for legitimate reasons with constructive feedback
- Communicate professionally and respectfully with Creators

### 10.3 Creator Responsibilities
When participating in collaborations as a Creator, you agree to:
- Submit content that meets the requirements specified in the collaboration agreement
- Deliver content within agreed-upon timelines
- Ensure all content complies with applicable laws, regulations, and platform guidelines
- Maintain connected TikTok and Stripe accounts in good standing
- Communicate professionally and respectfully with Brands
- Be truthful about your audience size, engagement metrics, and content capabilities

### 10.4 Collaboration Lifecycle
Collaborations follow this standard process:
1. **Proposal:** Either a Brand or Creator proposes a collaboration with specific terms
2. **Acceptance:** The other party reviews and accepts the collaboration proposal
3. **Funding:** The Brand funds the collaboration (payment held securely by Stripe)
4. **Content Creation:** The Creator produces the agreed-upon content
5. **Submission:** The Creator submits content through the platform for review
6. **Review:** The Brand reviews and approves or requests revisions to the content
7. **Completion:** Upon approval, funds are credited to the Creator's Stripe connected account (minus platform fee)
8. **Payout:** The Creator may request a payout at any time

### 10.5 Payment Timeline
Funds are transferred to Creator Stripe connected accounts immediately upon Brand approval of submitted content. Platform fees are deducted at the time of credit. Creators may request payouts at any time thereafter, subject to Stripe's standard payout schedules.

### 10.6 Communication
All collaboration-related communication should occur through the platform's messaging system. We reserve the right to monitor communications to ensure compliance with these Terms and to resolve disputes.

## 11. Refunds and Cancellations

### 11.1 Collaboration Cancellations
- Either party may decline or cancel a collaboration before it is accepted and funded
- Once a collaboration is funded by a Brand, it cannot be unilaterally cancelled
- If both parties agree to cancel a funded collaboration, please contact our support team
- Platform fees are non-refundable regardless of collaboration status

### 11.2 Content Rejection
If a Brand rejects submitted content, the collaboration remains open for the Creator to revise and resubmit. Brands must provide legitimate reasons for rejection and constructive feedback when possible. Repeated rejection without valid cause may result in dispute mediation or account review.

### 11.3 Non-Delivery
If a Creator fails to deliver agreed-upon content within 30 days after a collaboration is funded, Brands may request a refund through our support team. We will investigate the circumstances and may issue refunds at our sole discretion. Creators who repeatedly fail to deliver content may have their accounts suspended or terminated.

### 11.4 Disputes and Mediation
If a dispute arises between a Brand and Creator regarding collaboration terms, content quality, or payment, we may, at our discretion, provide mediation services. Our decisions in such matters are final. We reserve the right to withhold payments, issue refunds, or take other actions as necessary to resolve disputes fairly.

## 12. Tax Obligations

You are solely responsible for determining and paying any applicable taxes arising from your use of the Service, including income taxes, sales taxes, value-added taxes, and any other taxes or duties.

**For US-Based Creators:** We may be required to collect taxpayer identification information (such as your Social Security Number or Employer Identification Number) and issue IRS Form 1099-K or other tax forms as required by law when your earnings exceed applicable thresholds. You agree to provide accurate tax information when requested.

**For All Users:** You are responsible for:
- Determining your tax obligations in your jurisdiction
- Filing all required tax returns
- Paying all taxes owed
- Maintaining records of your transactions and earnings

We are not responsible for determining, collecting, reporting, or remitting taxes on your behalf, except as required by law. Consult with a tax professional regarding your specific tax obligations.

## 13. Disclaimer of Warranties

THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. WE EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE MAKE NO WARRANTY THAT THE SERVICE WILL MEET YOUR REQUIREMENTS, BE AVAILABLE ON AN UNINTERRUPTED, SECURE, OR ERROR-FREE BASIS, OR BE ACCURATE, RELIABLE, COMPLETE, LEGAL, OR SAFE.

## 14. Limitation of Liability

IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
- YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE
- ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE
- ANY CONTENT OBTAINED FROM THE SERVICE
- UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT

OUR TOTAL LIABILITY FOR ANY CLAIMS UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU HAVE PAID TO US IN THE PAST TWELVE MONTHS.

## 15. Indemnification

You agree to defend, indemnify, and hold harmless Collabish LLC and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including, without limitation, reasonable legal and accounting fees, arising out of or in any way connected with your access to or use of the Service or your violation of these Terms.

## 16. Disputes Between Users

Collabish is not responsible for disputes between Brands and Creators regarding collaboration terms, content quality, payment issues, or any other matters arising from user-to-user interactions. Users agree to resolve disputes in good faith and communicate professionally.

We may, at our sole discretion, provide mediation services or assistance in resolving disputes, but we are under no obligation to do so. Our decisions in such matters, if we choose to intervene, are final and binding.

We reserve the right to suspend or terminate accounts of users who repeatedly violate collaboration agreements, act in bad faith, or fail to resolve disputes professionally.

## 17. Term and Termination

### 17.1 Term
These Terms shall remain in full force and effect while you use the Service.

### 17.2 Termination by You
You may terminate your account at any time by following the instructions on the Service or by contacting us.

### 17.3 Termination by Us
We may suspend or terminate your access to the Service, your account, or these Terms at any time for any reason without notice or liability. Upon termination, your right to use the Service will immediately cease.

### 17.4 Effect of Termination
Any provisions of these Terms that by their nature should survive termination shall remain in full force and effect.

## 18. Modifications to the Service

We reserve the right to modify or discontinue, temporarily or permanently, the Service (or any part thereof) with or without notice. You agree that we shall not be liable to you or to any third party for any modification, suspension, or discontinuance of the Service.

## 19. Changes to These Terms

We may revise these Terms from time to time at our sole discretion. When we make changes, we will update the "Last Updated" date at the top of these Terms. Your continued use of the Service following the posting of revised Terms means that you accept and agree to the changes.

## 20. Governing Law and Dispute Resolution

These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.

Any dispute arising from or relating to the subject matter of these Terms shall be finally settled by arbitration in accordance with the rules of the American Arbitration Association, with the arbitration to take place in Delaware, United States.

## 21. Severability

If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.

## 22. Waiver

Our failure to enforce any right or provision of these Terms will not be considered a waiver of such right or provision. The waiver of any such right or provision will be effective only if in writing and signed by our authorized representative.

## 23. Entire Agreement

These Terms, together with our Privacy Policy and any other agreements expressly incorporated by reference herein, constitute the entire agreement between you and us regarding your use of the Service and supersede all prior and contemporaneous written or oral agreements between you and us.

## 24. Contact Information

If you have any questions about these Terms, please contact us at:

Collabish LLC

By using the Service, you acknowledge that you have read and understood these Terms and agree to be bound by them.`;
